import AgoraRTC from 'agora-rtc-sdk-ng';
import { CbEvents, SessionType } from '@openim/wasm-client-sdk';
import { imClient } from '../imClient';

const callSignalTypes = new Map([
  [CbEvents.OnInviteeAccepted, 'accepted'],
  [CbEvents.OnInviteeRejected, 'rejected'],
  [CbEvents.OnInvitationCancelled, 'cancelled'],
  [CbEvents.OnHangUp, 'hangup'],
  [CbEvents.OnInvitationTimeout, 'timeout'],
  [CbEvents.OnInviteeAcceptedByOtherDevice, 'answeredElsewhere'],
  [CbEvents.OnInviteeRejectedByOtherDevice, 'rejected']
]);

function parseEventData(value) {
  if (typeof value !== 'string') return value || {};
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function getPeer(data, fallbackUserID) {
  const participants = Array.isArray(data?.participant) ? data.participant : [data?.participant].filter(Boolean);
  const peer = participants.find(item => {
    const id = item?.userInfo?.userID || item?.userID;
    return id && id !== imClient.currentUser?.userId;
  }) || participants[0] || {};
  const user = peer?.userInfo || peer;
  return {
    userID: String(user?.userID || data?.userID || fallbackUserID || ''),
    name: String(user?.nickname || user?.name || user?.userID || data?.userID || fallbackUserID || ''),
    portrait: String(user?.faceURL || user?.portrait || '')
  };
}

class AgoraOpenIMProvider {
  constructor() {
    this.signalListeners = new Set();
    this.client = null;
    imClient.on('voiceCallEvent', this.handleOpenIMEvent.bind(this));
  }

  subscribeSignals(listener) {
    this.signalListeners.add(listener);
    return () => this.signalListeners.delete(listener);
  }

  emitSignal(event) {
    this.signalListeners.forEach(listener => listener(event));
  }

  invite(peerUserID, mediaType = 'audio') {
    return imClient.startVoiceCall(peerUserID, mediaType);
  }

  accept(invitation) {
    return imClient.acceptVoiceCall(invitation);
  }

  reject(invitation) {
    return imClient.rejectVoiceCall(invitation);
  }

  cancel(invitation) {
    return imClient.cancelVoiceCall(invitation);
  }

  hangUp(invitation) {
    return imClient.hangUpVoiceCall(invitation);
  }

  async connect({
    credentials,
    mediaType = 'audio',
    onParticipantConnected,
    onDisconnected,
    onVideoTracksChanged,
    onError
  }) {
    if (!credentials?.appID || !credentials?.channelName || !credentials?.token || credentials?.uid === undefined) {
      throw new Error('Agora 通话凭证不完整');
    }
    if (this.client) throw new Error('当前 Agora 通话尚未结束');

    const withVideo = mediaType === 'video';
    const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
    this.client = client;

    let localAudioTrack = null;
    let localVideoTrack = null;
    let remoteAudioTrack = null;
    let remoteVideoTrack = null;
    let localVideoElement = null;
    let remoteVideoElement = null;
    let attachedLocalVideoTrack = null;
    let attachedRemoteVideoTrack = null;
    let attachedLocalVideoElement = null;
    let attachedRemoteVideoElement = null;
    let joined = false;
    let closed = false;
    let cameraFacingMode = 'user';

    const renderVideoTrack = (track, element) => {
      if (track && element) track.play(element);
    };
    const syncLocalVideo = () => {
      if (attachedLocalVideoTrack === localVideoTrack && attachedLocalVideoElement === localVideoElement) return;
      attachedLocalVideoTrack?.stop();
      if (attachedLocalVideoElement) attachedLocalVideoElement.replaceChildren();
      attachedLocalVideoTrack = localVideoTrack;
      attachedLocalVideoElement = localVideoElement;
      renderVideoTrack(attachedLocalVideoTrack, attachedLocalVideoElement);
    };
    const syncRemoteVideo = () => {
      if (attachedRemoteVideoTrack === remoteVideoTrack && attachedRemoteVideoElement === remoteVideoElement) return;
      attachedRemoteVideoTrack?.stop();
      if (attachedRemoteVideoElement) attachedRemoteVideoElement.replaceChildren();
      attachedRemoteVideoTrack = remoteVideoTrack;
      attachedRemoteVideoElement = remoteVideoElement;
      renderVideoTrack(attachedRemoteVideoTrack, attachedRemoteVideoElement);
    };
    const setRemoteVideoTrack = track => {
      if (remoteVideoTrack === track) return;
      remoteVideoTrack = track || null;
      syncRemoteVideo();
      onVideoTracksChanged?.({ remoteVideoAvailable: Boolean(remoteVideoTrack) });
    };

    const dispose = async () => {
      if (closed) return;
      closed = true;
      if (this.client === client) this.client = null;
      client.removeAllListeners();
      const localTracks = [localAudioTrack, localVideoTrack].filter(Boolean);
      if (joined && localTracks.length) await client.unpublish(localTracks).catch(() => {});
      await client.leave().catch(() => {});
      remoteAudioTrack?.stop();
      remoteVideoTrack?.stop();
      localAudioTrack?.stop();
      localVideoTrack?.stop();
      localAudioTrack?.close();
      localVideoTrack?.close();
      localVideoElement?.replaceChildren();
      remoteVideoElement?.replaceChildren();
      attachedLocalVideoTrack = null;
      attachedRemoteVideoTrack = null;
      attachedLocalVideoElement = null;
      attachedRemoteVideoElement = null;
      remoteAudioTrack = null;
      remoteVideoTrack = null;
    };

    const subscribeToPublishedTrack = async (user, trackType) => {
      try {
        await client.subscribe(user, trackType);
        if (closed) return;
        if (trackType === 'audio' && user.audioTrack) {
          remoteAudioTrack?.stop();
          remoteAudioTrack = user.audioTrack;
          remoteAudioTrack.play();
        } else if (trackType === 'video' && user.videoTrack) {
          setRemoteVideoTrack(user.videoTrack);
        }
        onParticipantConnected?.();
      } catch (error) {
        onError?.(error);
      }
    };

    const handleUserUnpublished = (user, trackType) => {
      if (trackType === 'audio') {
        remoteAudioTrack?.stop();
        remoteAudioTrack = null;
      } else if (trackType === 'video') {
        setRemoteVideoTrack(null);
      }
    };

    const renewToken = async () => {
      try {
        const refreshed = await imClient.renewVoiceCallToken(credentials.roomID);
        if (!closed) await client.renewToken(refreshed.token);
      } catch (error) {
        onError?.(error);
      }
    };

    client.on('user-joined', () => onParticipantConnected?.());
    client.on('user-published', subscribeToPublishedTrack);
    client.on('user-unpublished', handleUserUnpublished);
    client.on('user-left', () => {
      remoteAudioTrack?.stop();
      remoteAudioTrack = null;
      setRemoteVideoTrack(null);
    });
    client.on('token-privilege-will-expire', renewToken);
    client.on('token-privilege-did-expire', renewToken);
    client.on('connection-state-change', (state, _previousState, reason) => {
      if (state === 'DISCONNECTED' && joined && !closed) {
        void dispose().finally(() => onDisconnected?.(reason));
      }
    });

    try {
      await client.join(credentials.appID, credentials.channelName, credentials.token, String(credentials.uid));
      joined = true;
      localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      if (withVideo) {
        localVideoTrack = await AgoraRTC.createCameraVideoTrack({ encoderConfig: '480p_1' });
        try {
          await localVideoTrack.setDevice({ facingMode: 'user' });
        } catch {
          // Some desktops expose only one camera; keep the camera already opened by the SDK.
        }
      }
      await client.publish([localAudioTrack, ...(localVideoTrack ? [localVideoTrack] : [])]);
      syncLocalVideo();
      onVideoTracksChanged?.();
      if (client.remoteUsers.length) onParticipantConnected?.();
    } catch (error) {
      await dispose();
      throw error;
    }

    return {
      get cameraFacingMode() {
        return cameraFacingMode;
      },
      setMuted: muted => localAudioTrack.setEnabled(!muted),
      setCameraEnabled: async enabled => {
        if (!withVideo || !localVideoTrack) return false;
        await localVideoTrack.setEnabled(Boolean(enabled));
        return Boolean(enabled);
      },
      switchCamera: async () => {
        if (!withVideo || !localVideoTrack) throw new Error('当前不是视频通话');
        const nextFacingMode = cameraFacingMode === 'user' ? 'environment' : 'user';
        try {
          await localVideoTrack.setDevice({ facingMode: nextFacingMode });
        } catch (facingError) {
          const cameras = await AgoraRTC.getCameras();
          const currentID = localVideoTrack.getMediaStreamTrack()?.getSettings?.().deviceId;
          const currentIndex = cameras.findIndex(camera => camera.deviceId === currentID);
          if (cameras.length < 2) throw facingError;
          const nextCamera = cameras[(currentIndex + 1 + cameras.length) % cameras.length];
          await localVideoTrack.setDevice(nextCamera.deviceId);
        }
        cameraFacingMode = nextFacingMode;
        return cameraFacingMode;
      },
      attachVideoElements: (localElement, remoteElement) => {
        localVideoElement = localElement || null;
        remoteVideoElement = remoteElement || null;
        syncLocalVideo();
        syncRemoteVideo();
      },
      disconnect: dispose
    };
  }

  handleOpenIMEvent(event) {
    if (event?.type === 'rtcCallSignal') {
      const signal = event?.data || {};
      const currentUserID = String(imClient.currentUser?.userId || '');
      const invitation = signal.invitation;
      const roomID = String(invitation?.roomID || '');
      if (!roomID || signal.targetUserID !== currentUserID || signal.senderUserID === currentUserID) return;
      if (signal.signal === 'invite') {
        if (!['audio', 'video'].includes(invitation?.mediaType)) return;
        const safeInvitation = {
          ...invitation,
          inviterUserID: signal.senderUserID,
          inviteeUserIDList: [currentUserID],
          roomID,
          sessionType: invitation.sessionType || SessionType.Single
        };
        const peer = getPeer({ userID: signal.senderUserID }, signal.senderUserID);
        this.emitSignal({ type: 'incoming', invitation: safeInvitation, roomID, peer });
        return;
      }
      const signalTypes = {
        accept: 'accepted',
        reject: 'rejected',
        cancel: 'cancelled',
        hangup: 'hangup',
        timeout: 'timeout'
      };
      const type = signalTypes[signal.signal];
      if (type) this.emitSignal({ type, invitation, roomID });
      return;
    }
    const data = parseEventData(event?.data);
    const invitation = data?.invitation;
    if (event?.errCode && Number(event.errCode) !== 0) return;

    if (event?.type === CbEvents.OnReceiveNewInvitation) {
      if (!['audio', 'video'].includes(invitation?.mediaType)) {
        if (invitation) this.reject(invitation).catch(() => {});
        return;
      }
      const peer = getPeer(data, invitation?.inviterUserID);
      this.emitSignal({
        type: 'incoming',
        invitation,
        roomID: String(invitation?.roomID || data?.roomID || ''),
        peer
      });
      return;
    }

    const type = callSignalTypes.get(event?.type);
    if (!type) return;
    this.emitSignal({
      type,
      invitation,
      roomID: String(data?.roomID || invitation?.roomID || '')
    });
  }
}

export const agoraOpenIMProvider = new AgoraOpenIMProvider();
